import Image from '@tiptap/extension-image';
import { VueNodeViewRenderer } from '@tiptap/vue-2';
import NodeViewImageWithResize from './NodeViewImageWithResize.vue';

export const extenstionKey = 'imageResize';

export const ImageResize = Image.extend({
  name: extenstionKey,

  addOptions() {
    return {
      ...this.parent?.(),
      onUploadImage: undefined,
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      height: {
        default: null,
        parseHTML: element => {
          const height = element.getAttribute('height') || element.style.height;
          return height ? Number.parseInt(height, 10) : null;
        },
        renderHTML: attributes => {
          if (!attributes.height) return {};
          return {
            height: attributes.height,
            style: `height: ${attributes.height}px; width: auto;`,
          };
        },
      },
      width: {
        default: null,
        parseHTML: element => {
          const width = element.getAttribute('width') || element.style.width;
          return width ? Number.parseInt(width, 10) : null;
        },
        renderHTML: attributes => {
          if (!attributes.width) return {};
          return {
            width: attributes.width,
            style: `width: ${attributes.width}px;`,
          };
        },
      },
      'data-uploading': {
        default: null,
        parseHTML: element => element.getAttribute('data-uploading'),
        renderHTML: attributes => {
          if (!attributes['data-uploading']) return {};
          return { 'data-uploading': attributes['data-uploading'] };
        },
      },
      'data-upload-error': {
        default: null,
        parseHTML: element => element.getAttribute('data-upload-error'),
        renderHTML: attributes => {
          if (!attributes['data-upload-error']) return {};
          return { 'data-upload-error': attributes['data-upload-error'] };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]',
        // Higher priority to ensure this extension handles all images
        priority: 100,
        getAttrs: element => {
          const isUploading = element.getAttribute('data-uploading') === 'true';
          const uploadError =
            element.getAttribute('data-upload-error') === 'true';
          const src = element.getAttribute('src');

          // Block local file URLs (file://) from being parsed
          // This prevents pasted local screenshots from appearing as broken images
          if (src && src.startsWith('file://')) {
            return false; // Reject this image node
          }

          // Handle all images, including web images
          return {
            src,
            alt: element.getAttribute('alt'),
            title: element.getAttribute('title'),
            width:
              element.getAttribute('width') || element.style.width
                ? Number.parseInt(
                    element.getAttribute('width') || element.style.width,
                    10
                  )
                : null,
            height:
              element.getAttribute('height') || element.style.height
                ? Number.parseInt(
                    element.getAttribute('height') || element.style.height,
                    10
                  )
                : null,
            'data-uploading': isUploading ? 'true' : null,
            'data-upload-error': uploadError ? 'true' : null,
          };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { src, alt, title, width, height } = node.attrs;
    const isUploading = node.attrs['data-uploading'];
    const uploadError = node.attrs['data-upload-error'];

    const attrs = {
      src,
      class: 'tiptap-image-resize',
      ...HTMLAttributes,
    };

    if (alt) attrs.alt = alt;
    if (title) attrs.title = title;
    if (height) {
      attrs.height = height;
      attrs.style = `height: ${height}px; width: auto;`;
    }
    if (width && !height) {
      attrs.width = width;
      attrs.style = `width: ${width}px;`;
    }
    if (isUploading) attrs['data-uploading'] = 'true';
    if (uploadError) attrs['data-upload-error'] = 'true';

    return ['img', attrs];
  },

  addNodeView() {
    return VueNodeViewRenderer(NodeViewImageWithResize);
  },

  addCommands() {
    return {
      ...this.parent?.(),
      insertImageResize:
        attributes =>
        ({ chain, editor }) => {
          if (!attributes || !attributes.src) {
            console.error('Cannot insert image without src attribute.');
            return false;
          }

          // Insert image, then insert a paragraph and move cursor to it
          return chain()
            .insertContent({
              type: this.name,
              attrs: attributes,
            })
            .command(({ tr, dispatch }) => {
              if (dispatch) {
                const paragraph = editor.schema.nodes.paragraph.create();
                tr.insert(tr.selection.to, paragraph);
                // Move cursor into the new paragraph
                tr.setSelection(
                  editor.state.selection.constructor.near(
                    tr.doc.resolve(tr.selection.to)
                  )
                );
              }
              return true;
            })
            .run();
        },

      updateImageSize:
        (width, height = null) =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const { from } = selection;

          // Find the image node at the current selection
          const node = state.doc.nodeAt(from);

          if (node && node.type.name === this.name) {
            const attrs = { ...node.attrs, width, height };

            if (dispatch) {
              tr.setNodeMarkup(from, null, attrs);
              dispatch(tr);
            }
            return true;
          }

          return false;
        },

      resizeImageTo:
        (width, height = null) =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;

          // Find all image nodes in the current selection
          state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (node.type.name === this.name) {
              const attrs = { ...node.attrs, width, height };
              tr.setNodeMarkup(pos, null, attrs);
            }
          });

          if (dispatch) {
            dispatch(tr);
          }
          return true;
        },

      // Enhanced command that works with both standard image and imageResize
      setImageAttributes:
        attributes =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const { from, to } = selection;

          let updated = false;

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.type.name === this.name || node.type.name === 'image') {
              const attrs = { ...node.attrs, ...attributes };
              tr.setNodeMarkup(pos, null, attrs);
              updated = true;
            }
          });

          if (dispatch && updated) {
            dispatch(tr);
          }
          return updated;
        },
    };
  },

  addStorage() {
    return {
      markdown: {
        // How to serialize this node to Markdown
        serialize: (state, node) => {
          const { src, alt, title, height } = node.attrs;

          // Build the image URL with cw_image_height query parameter if height is specified
          let imageSrc = src;
          if (height) {
            const url = new URL(src, window.location.origin);
            url.searchParams.set('cw_image_height', `${height}px`);
            imageSrc = url.toString();
          }

          // Basic markdown image syntax
          let markdown = `![${alt || ''}](${imageSrc}`;

          if (title) {
            markdown += ` "${title}"`;
          }

          markdown += ')';

          state.write(markdown);
          // Add a newline after each image to ensure proper rendering
          state.write('\n\n');
        },

        // How to parse this node from Markdown
        parse: {
          setup(markdownit) {
            markdownit.renderer.rules.image = (tokens, idx) => {
              const token = tokens[idx];
              const src = token.attrGet('src');
              const alt = token.content;
              const title = token.attrGet('title');

              // Extract height from cw_image_height query parameter
              let height = null;
              if (src) {
                try {
                  const url = new URL(src, window.location.origin);
                  const heightParam = url.searchParams.get('cw_image_height');
                  if (heightParam) {
                    // Extract numeric value from '48px' or '48'
                    const heightMatch = heightParam.match(/^(\d+)/);
                    if (heightMatch) {
                      height = Number.parseInt(heightMatch[1], 10);
                    }
                  }
                } catch (e) {
                  // Invalid URL, ignore
                }
              }

              // Create attributes for our custom image node
              let attrs = `data-type="image-resize" src="${src}"`;
              if (alt) attrs += ` alt="${alt}"`;
              if (title) attrs += ` title="${title}"`;
              if (height)
                attrs += ` height="${height}" style="height: ${height}px; width: auto;"`;

              attrs += ` class="tiptap-image-resize"`;

              return `<img ${attrs}>`;
            };
          },
        },
      },
      onUploadImage: this.options.onUploadImage,
    };
  },

  addInputRules() {
    return [
      ...(this.parent?.() || []),
      {
        find: /!\[([^\]]*)\]\(([^)]+)\)$/,
        handler: ({ state, range, match }) => {
          const [, alt, src] = match;
          const { tr } = state;
          const start = range.from;
          const end = range.to;

          tr.replaceWith(
            start,
            end,
            this.type.create({
              src,
              alt,
            })
          );

          return tr;
        },
      },
    ];
  },
});
